import { ChangeEvent, ChangeEventHandler, KeyboardEvent } from 'react';
import { ControllerFieldState, FieldErrors } from 'react-hook-form';
import { flatMap, isBoolean, isEqual, isEqualWith, isUndefined, sortBy } from 'lodash-es';
import { RefinementCtx, z } from 'zod';
import { AutocompleteItem, SelectItem } from '@ds';
import { SelectItemType } from '@/types';
import { DATE_REGEX, TIME_REGEX } from '@/constants/date.constants';
import { MAX_LENGTH_2000 } from '@/constants/form.constants';

export const renderSelectItem = (item: object) => {
  const selectItem = item as SelectItemType;
  return <SelectItem key={selectItem.value}>{selectItem.name}</SelectItem>;
};

export const renderAutocompleteItem = (item: object) => {
  const data = item as SelectItemType;
  return (
    <AutocompleteItem key={data.value} className="*:text-wrap">
      {data.name}
    </AutocompleteItem>
  );
};

type ComparatorFunction<T extends Record<string, unknown>> = (
  key: keyof T,
) => (value: T[keyof T], other: T[keyof T]) => boolean | undefined;

export const remapKeys = <T extends Record<string, unknown>>(
  obj: Partial<T>,
  mapping: Partial<Record<keyof T, string>>,
): Record<string, unknown> =>
  Object.fromEntries(Object.entries(obj).map(([key, value]) => [mapping[key as keyof T] ?? key, value]));

export const getChangedValues = <T extends Record<string, unknown>>(
  data: T,
  origData: T,
  comparator?: ComparatorFunction<T>,
): Partial<T> => {
  return Object.keys(data).reduce((acc, key) => {
    const isDataEqual = comparator
      ? isEqualWith(data[key], origData[key], comparator(key))
      : isEqual(data[key], origData[key]);

    if (!isDataEqual) {
      return {
        ...acc,
        [key]: data[key],
      };
    }

    return acc;
  }, {} as Partial<T>);
};

export const getInputValidationErrorProps = (fieldState: ControllerFieldState, contextError?: { message?: string }) => {
  const { isTouched, invalid, error } = fieldState;
  const hasContextError = !!contextError?.message;

  return {
    isInvalid: (isTouched && invalid) || hasContextError,
    errorMessage: (isTouched ? error?.message : undefined) || contextError?.message,
  };
};

const KEEP_EMPTY_STRING_KEYS = new Set(['serviceDetails']);

export const replaceEmptyWithNull = <T extends Record<string, unknown>>(obj: T): T => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      if (KEEP_EMPTY_STRING_KEYS.has(key) && value === '') {
        return [key, ''];
      }

      const isFalsyNonBoolean = !value && !isBoolean(value);
      const newValue = isFalsyNonBoolean ? null : value;
      return [key, newValue];
    }),
  ) as T;
};

/**
 * Checks if any of the specified keys have updates (are not undefined).
 * If no updates exist, returns null.
 * Otherwise returns an object with each key resolved to either the updated value or the fallback value.
 */
export const resolveUpdatesOrNull = <T extends Record<string, unknown>>(
  updates: Partial<T>,
  fallbackValues: T,
  keys: (keyof T)[],
): Pick<T, (typeof keys)[number]> | null => {
  const hasUpdates = keys.some((key) => !isUndefined(updates[key]));

  if (!hasUpdates) return null;

  return Object.fromEntries(
    keys.map((key) => [key, isUndefined(updates[key]) ? fallbackValues[key] : updates[key]]),
  ) as Pick<T, (typeof keys)[number]>;
};

export const sortAlphabetically = <T extends Record<string, unknown>>(list: T[], property: keyof T = 'name'): T[] => {
  return sortBy(list, (item) => String(item[property]).toLowerCase());
};

export const preventNonNumericKeys = (e: KeyboardEvent<HTMLInputElement>): boolean => {
  if (e.key === 'e' || e.key === '+' || e.key === '-') {
    e.preventDefault();
    return false;
  }
  return true;
};

export const preventNonIntKeys = (event: KeyboardEvent<HTMLDivElement>): boolean => {
  if (['e', 'E', '+', '-', '.', ','].includes(event.key)) {
    event.preventDefault();
    return false;
  }
  return true;
};

export const createNumericChangeHandler = <T extends HTMLInputElement>(
  handler: ChangeEventHandler<T>,
  maxDigits: number = 16,
): ChangeEventHandler<T> => {
  return (event: ChangeEvent<T>) => {
    const newValue = event.target.value;

    if (newValue.length > maxDigits || Number.isNaN(Number(newValue))) {
      return;
    }

    const modifiedEvent: ChangeEvent<T> = {
      ...event,
      target: {
        ...event.target,
        value: Number(newValue),
      },
    };

    handler(modifiedEvent);
  };
};

export const getErrorMessages = (errors: FieldErrors): string[] => {
  const errorsArray = flatMap(errors, (e) => {
    if (typeof e?.message === 'string') {
      return e.message;
    }

    return getErrorMessages(e as FieldErrors);
  });

  const uniqErrorsSet = new Set(errorsArray);

  return Array.from(uniqErrorsSet);
};

type AddCustomZodErrorsProps = {
  ctx: RefinementCtx;
  path: (string | number)[];
  message: string;
};

export const addCustomZodErrors = ({ ctx, path, message }: AddCustomZodErrorsProps) => {
  ctx.addIssue({
    code: 'custom',
    path,
    message,
  });
};

type OpoContactInvitationBaseFields = {
  isOpoUser?: boolean;
  inviteAsUser?: boolean;
  firstName?: string;
  lastName?: string;
  email?: string | null;
};

const validateOpoContactInvitationBaseFields = (data: OpoContactInvitationBaseFields, ctx: RefinementCtx) => {
  const isRequired = data.isOpoUser || data.inviteAsUser;
  if (!isRequired) return false;

  if (!data.firstName?.trim()) {
    addCustomZodErrors({ ctx, path: ['firstName'], message: 'First name is required' });
  }
  if (!data.lastName?.trim()) {
    addCustomZodErrors({ ctx, path: ['lastName'], message: 'Last name is required' });
  }
  if (!data.email) {
    addCustomZodErrors({ ctx, path: ['email'], message: 'Email is required' });
  } else if (!z.email().safeParse(data.email).success) {
    addCustomZodErrors({ ctx, path: ['email'], message: 'Enter a valid email address' });
  }

  return true;
};

/** For forms with a flat `roles` field (nop-access). */
type OpoContactInvitationFields = OpoContactInvitationBaseFields & {
  roles?: unknown[];
};

export const createOpoContactInvitationValidation = (data: OpoContactInvitationFields, ctx: RefinementCtx) => {
  const isRequired = validateOpoContactInvitationBaseFields(data, ctx);
  if (!isRequired) return;

  if (!data.roles?.length) {
    addCustomZodErrors({ ctx, path: ['roles'], message: 'At least one role is required' });
  }
};

/** For forms with a `memberships` array (nop-connect). */
type OpoContactMembershipFields = OpoContactInvitationBaseFields & {
  memberships?: Array<{ opoOrganizationId?: string; roles?: unknown[] }>;
};

export const createOpoContactMembershipValidation = (data: OpoContactMembershipFields, ctx: RefinementCtx) => {
  const isRequired = validateOpoContactInvitationBaseFields(data, ctx);
  if (!isRequired) return;

  data.memberships?.forEach((membership, index) => {
    if (membership.opoOrganizationId && !membership.roles?.length) {
      addCustomZodErrors({ ctx, path: ['memberships', index, 'roles'], message: 'At least one role is required' });
    }
  });
};

type DateTimeFieldKeys = {
  date: string;
  time: string;
  timezone: string;
};

type DateTimeFieldOptions = {
  nullish?: boolean;
  nullable?: boolean;
  optional?: boolean;
};

export const createDateTimeFields = <K extends DateTimeFieldKeys>(keys: K, options?: DateTimeFieldOptions) => {
  const baseDate = z.string().min(1, { message: 'Specify date' }).regex(DATE_REGEX, { message: 'Invalid date format' });
  const baseTime = z.string().min(1, { message: 'Specify time' }).regex(TIME_REGEX, { message: 'Invalid time format' });
  const baseTimezone = z.string().min(1, { message: 'Select timezone' });

  if (options?.nullish) {
    return {
      [keys.date]: z.union([baseDate, z.null(), z.undefined()]),
      [keys.time]: z.union([baseTime, z.null(), z.undefined()]),
      [keys.timezone]: z.union([baseTimezone, z.null(), z.undefined()]),
    } as {
      [P in K['date']]: z.ZodUnion<[z.ZodString, z.ZodNull, z.ZodUndefined]>;
    } & {
      [P in K['time']]: z.ZodUnion<[z.ZodString, z.ZodNull, z.ZodUndefined]>;
    } & {
      [P in K['timezone']]: z.ZodUnion<[z.ZodString, z.ZodNull, z.ZodUndefined]>;
    };
  }

  if (options?.nullable) {
    return {
      [keys.date]: z.union([baseDate, z.null()]),
      [keys.time]: z.union([baseTime, z.null()]),
      [keys.timezone]: z.union([baseTimezone, z.null()]),
    } as {
      [P in K['date']]: z.ZodUnion<[z.ZodString, z.ZodNull]>;
    } & {
      [P in K['time']]: z.ZodUnion<[z.ZodString, z.ZodNull]>;
    } & {
      [P in K['timezone']]: z.ZodUnion<[z.ZodString, z.ZodNull]>;
    };
  }

  if (options?.optional) {
    return {
      [keys.date]: z.union([baseDate, z.undefined()]),
      [keys.time]: z.union([baseTime, z.undefined()]),
      [keys.timezone]: z.union([baseTimezone, z.undefined()]),
    } as {
      [P in K['date']]: z.ZodUnion<[z.ZodString, z.ZodUndefined]>;
    } & {
      [P in K['time']]: z.ZodUnion<[z.ZodString, z.ZodUndefined]>;
    } & {
      [P in K['timezone']]: z.ZodUnion<[z.ZodString, z.ZodUndefined]>;
    };
  }

  return {
    [keys.date]: baseDate,
    [keys.time]: baseTime,
    [keys.timezone]: baseTimezone,
  } as {
    [P in K['date']]: z.ZodString;
  } & {
    [P in K['time']]: z.ZodString;
  } & {
    [P in K['timezone']]: z.ZodString;
  };
};

export const isCommentTooLong = (value?: string, length?: number) => (value?.length ?? 0) > (length ?? MAX_LENGTH_2000);
